import GuestReservationService from '../services/GuestReservationService'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { StorePerformance } from '@/models'
import { parsePerformance } from '@/models'
import PerformanceService from '@/modules/performance/services/PerformanceService'
import {
  EnrichedAddOn,
  EnrichedTable,
  OrderPricing,
  getPricing,
  priceAddOns,
  tablesForSeatedParty,
} from '@/orders'
import { useSettingStore } from '@/stores/setting'

import {
  PerformanceSeatingTimeId,
  PerformanceTableId,
  PromotionCodeRead,
  ReservationAddOnWrite,
  ReservationDetailedRead,
} from '@generated/types'

export interface Contact {
  email: string
  phone: string
  first_name: string
  last_name: string
  accepts_marketing: boolean
}

const useOrderConfirmationStore = defineStore('orderConfirmation', () => {
  const performance = ref<StorePerformance | null>(null)
  const reservation = ref<ReservationDetailedRead | null>(null)
  const table_ids = ref<PerformanceTableId[] | null>(null)
  const reservation_key = ref<string | null>(null)
  const seating_time = ref<{ id: PerformanceSeatingTimeId; time: string; datetime: string } | null>(
    null,
  )
  const number_of_guests = ref<number>(0)
  const add_ons = ref<ReservationAddOnWrite[] | null>(null)
  const contact = ref<Contact | null>(null)
  const promo_code = ref<PromotionCodeRead | null>(null)

  async function loadOrderDetails(reservationId: string, reservationKey: string) {
    const response = await GuestReservationService.fetchByIdAndKey(reservationId, reservationKey)
    const query = response.data.invite_code_code
      ? { invite_code: response.data.invite_code_code }
      : undefined
    const performanceResponse = await PerformanceService.fetchOne(
      response.data.performance_id,
      query,
    )
    performance.value = parsePerformance(performanceResponse.data)
    reservation.value = response.data
    table_ids.value = response.data.tables
    add_ons.value = response.data.add_ons
    number_of_guests.value = response.data.number_of_guests
    seating_time.value =
      performance.value.seating_times?.find(st => st.id === response.data.seating_time_id) || null
    contact.value = response.data.contact
    promo_code.value = response.data.promotion_code
  }

  async function refreshReservation(reservationId: string, reservationKey: string) {
    const response = await GuestReservationService.fetchByIdAndKey(reservationId, reservationKey)
    reservation.value = response.data
    table_ids.value = response.data.tables
    add_ons.value = response.data.add_ons
    number_of_guests.value = response.data.number_of_guests
    seating_time.value =
      performance.value?.seating_times?.find(st => st.id === response.data.seating_time_id) ?? null
    contact.value = response.data.contact
    promo_code.value = response.data.promotion_code
  }

  const settingStore = useSettingStore()
  const actualServiceFee = computed<number>(() => {
    if (!performance.value || performance.value.service_fee === null) {
      return settingStore.service_fee
    }
    return Number(performance.value.service_fee)
  })
  const actualServicePercentage = computed<number>(() => {
    if (!performance.value || performance.value.service_percentage === null) {
      return settingStore.service_percentage
    }
    return Number(performance.value.service_percentage)
  })

  const tableModels = computed<EnrichedTable[]>(() => {
    if (!table_ids.value) return []
    return tablesForSeatedParty(performance.value, table_ids.value, number_of_guests.value)
  })
  const addOnsModels = computed<EnrichedAddOn[]>(() => {
    if (!performance.value?.add_ons) return []
    return priceAddOns(performance.value.add_ons, add_ons.value)
  })
  const percentOff = computed<number>(() => {
    return Number(promo_code.value?.coupon?.percent_off || 0)
  })

  const pricing = computed<OrderPricing>(() => {
    return getPricing({
      tables: tableModels.value,
      addOns: addOnsModels.value,
      numberOfGuests: number_of_guests.value,
      serviceFeePerGuest: actualServiceFee.value,
      servicePercentage: actualServicePercentage.value,
      taxRate: settingStore.tax_rate,
      feeTaxRate: Number(settingStore.fee_tax_rate),
      percentOff: percentOff.value,
      applicationFee: settingStore.application_fee,
      applicationFeePerReservation: settingStore.application_fee_per_reservation,
    })
  })

  return {
    performance,
    reservation,
    table_ids,
    reservation_key,
    seating_time,
    number_of_guests,
    add_ons,
    contact,
    promo_code,
    pricing,
    tableModels,
    addOnsModels,
    percentOff,
    actualServiceFee,
    actualServicePercentage,
    loadOrderDetails,
    refreshReservation,
  }
})

export default useOrderConfirmationStore
